import React, { useEffect, useState } from "react";
import "../../assets/css/Actualites/Actualites_Poste.css";
import { API_BASE_URL } from "../../utils/config";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faSuitcaseMedical,
    faGraduationCap,
    faHandshakeAngle,
    faHandHoldingHeart,
    faGears,
} from "@fortawesome/free-solid-svg-icons";

function EventsSection() {
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true); // Loader pour afficher le chargement
    const [currentPage, setCurrentPage] = useState(1); // Page actuelle
    const [totalPages, setTotalPages] = useState(1); // Nombre total de pages
    const [filterAntenne, setFilterAntenne] = useState(""); // Filtre pour l'antenne
    const [filterType, setFilterType] = useState(""); // Filtre pour le type

    const fetchEvents = async (page = 1) => {
        try {
            setLoading(true);
            
            // Construire l'URL avec filtres
            let url = `${API_BASE_URL}/news/?page=${page}&per_page=15`;
            if (filterAntenne) url += `&antenne=${encodeURIComponent(filterAntenne)}`;
            if (filterType) url += `&type=${encodeURIComponent(filterType)}`;
    
            const response = await fetch(url);
            if (!response.ok) throw new Error("Erreur lors de la récupération des événements.");
            
            const data = await response.json();
            setEvents(data.news);
            setTotalPages(data.pages);
            setCurrentPage(data.page);
        } catch (error) {
            console.error("Erreur lors de la récupération des événements:", error);
        } finally {
            setLoading(false);
        }
    };
    
    // Charger les données à chaque changement de page OU de filtre
    useEffect(() => {
        fetchEvents(1); // On relance la requête avec page 1 lors du filtrage
    }, [filterAntenne, filterType]);
    
    useEffect(() => {
        fetchEvents(currentPage);
    }, [currentPage]);    

    const getIconAndColor = (eventType) => {
        switch (eventType) {
            case "Dispositif de premiers secours":
                return { icon: faSuitcaseMedical, color: "orange" };
            case "Formation":
                return { icon: faGraduationCap, color: "green" };
            case "Soutien aux populations":
                return { icon: faHandHoldingHeart, color: "red" };
            case "Renfort pour les secours publics":
                return { icon: faHandshakeAngle, color: "blue" };
            case "Exercice":
                return { icon: faGears, color: "gray" };
            default:
                return { icon: null, color: "gray" };
        }
    };

    const handlePageChange = (page) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    return (
        <div className="events-section container my-5">
            <h2 className="section-title">Nos Événements</h2>

            {/* Filtres */}
            <div className="filters mb-4 d-flex justify-content-between">
                <select
                    className="form-select me-2"
                    value={filterAntenne}
                    onChange={(e) => setFilterAntenne(e.target.value)}
                >
                    <option value="">Toutes les antennes</option>
                    {[...new Set(events.map((event) => event.antenne_nom))].map(
                        (antenneName, index) =>
                            antenneName && (
                                <option key={index} value={antenneName}>
                                    {antenneName}
                                </option>
                            )
                    )}
                </select>

                <select
                    className="form-select"
                    value={filterType}
                    onChange={(e) => setFilterType(e.target.value)}
                >
                    <option value="">Tous les types</option>
                    {[...new Set(events.map((event) => event.type))].map(
                        (type, index) =>
                            type && (
                                <option key={index} value={type}>
                                    {type}
                                </option>
                            )
                    )}
                </select>
            </div>

            {/* Loader */}
            {loading ? (
                <div className="row">
                    {[...Array(6)].map((_, index) => (
                        <div key={index} className="col-md-4 mb-4">
                            <div className="event-tile-placeholder">
                                <div className="event-media-placeholder"></div>
                                <div className="event-content-placeholder">
                                    <div className="placeholder-title"></div>
                                    <div className="placeholder-line"></div>
                                    <div className="placeholder-line"></div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <div className="row">
                    {events
                        .filter(
                            (event) =>
                                (!filterAntenne || event.antenne_nom === filterAntenne) &&
                                (!filterType || event.type === filterType)
                        )
                        .map((event) => {
                            const { icon, color } = getIconAndColor(event.type);

                            return (
                                <div key={event.id} className="col-md-4 mb-4">
                                    <div className="event-tile">
                                        <div className="event-tags">
                                            {event.antenne_nom && (
                                                <span className="event-tag">
                                                    {event.antenne_nom}
                                                </span>
                                            )}
                                            {icon && (
                                                <span
                                                    className="event-tag"
                                                    style={{ backgroundColor: color }}
                                                >
                                                    <FontAwesomeIcon icon={icon} />
                                                </span>
                                            )}
                                        </div>
                                        <div className="event-media">
                                            <img
                                                src={`${event.media || `https://storage.croixblanche-42.org/sfcb42-website/events/${event.id}.webp`}`}
                                                alt={event.titre}
                                            />
                                        </div>
                                        <div className="event-content">
                                            <h5 className="event-title">{event.titre}</h5>
                                            <p className="event-date">
                                                {event.date_debut === event.date_fin
                                                    ? event.date_debut
                                                    : `Du ${event.date_debut} au ${event.date_fin}`}
                                            </p>
                                            <p className="event-description">{event.description}</p>
                                            {event.liens && event.liens.length > 0 && (
                                                <div className="event-links">
                                                    {event.liens.map((link, index) => (
                                                        <a
                                                            key={index}
                                                            href={link.url}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="event-link"
                                                        >
                                                            {link.label}
                                                        </a>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                </div>
            )}

            {/* Pagination */}
            <div className="pagination mt-4">
                <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="btn btn-primary"
                >
                    Précédent
                </button>
                <span className="mx-3">
                    Page {currentPage} sur {totalPages}
                </span>
                <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="btn btn-primary"
                >
                    Suivant
                </button>
            </div>
        </div>
    );
}

export default EventsSection;
